import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Modal,
  Popconfirm,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
  Grid
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  SendOutlined,
  SyncOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { whatsappApi } from '../utils/api';
import ColumnsCardList from '../components/common/ColumnsCardList';

const { Title, Text, Paragraph } = Typography;

/**
 * Formate une date provenant du serveur dans le fuseau du navigateur afin que
 * l'utilisateur sache exactement quand le message a été créé ou programmé.
 * @param {string|Date|null} date Date ISO retournée par l'API ou date locale de mise à jour.
 * @returns {string} Date lisible ou un tiret si la donnée est absente.
 */
const formatDate = (date) => (date ? dayjs(date).format('DD/MM/YYYY à HH:mm') : '—');

/**
 * Détermine le badge visuel d'un statut d'historique. Le texte reste toujours
 * présent : la couleur n'est donc pas le seul moyen de comprendre l'état.
 * @param {string} status Statut brut enregistré par le système de queue.
 * @returns {React.ReactNode} Étiquette Ant Design correspondant au statut.
 */
const renderHistoryStatus = (status) => {
  const definitions = {
    sent: { color: 'success', label: 'Envoyé' },
    failed: { color: 'error', label: 'Échec' },
    cancelled: { color: 'default', label: 'Annulé' },
    blocked: { color: 'warning', label: 'Bloqué' }
  };
  const definition = definitions[status] || { color: 'default', label: status || 'Inconnu' };
  return <Tag color={definition.color}>{definition.label}</Tag>;
};

/**
 * Affiche le suivi opérationnel de la file WhatsApp. Les messages en attente
 * peuvent être annulés avant leur traitement ; l'historique est en lecture
 * seule pour conserver la traçabilité des envois et des échecs déjà produits.
 */
const WhatsAppQueue = () => {
  // Mobile (< md) : cartes à la place des tableaux ; `=== false` évite un flash avant la mesure
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const [stats, setStats] = useState(null);
  const [pendingMessages, setPendingMessages] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);

  /**
   * Charge en parallèle les trois sources affichées par la page afin que les
   * indicateurs, la liste d'attente et l'historique reflètent le même instant.
   * En cas d'erreur, les données déjà visibles sont conservées pour éviter une
   * page vide et l'utilisateur reçoit un retour explicite.
   * @param {boolean} afficherChargement Indique si le premier chargement doit occuper la page.
   */
  const loadQueueData = useCallback(async (afficherChargement = false) => {
    if (afficherChargement) setLoading(true);
    else setRefreshing(true);

    try {
      const [statsResponse, pendingResponse, historyResponse] = await Promise.all([
        whatsappApi.getQueueStats(),
        whatsappApi.getPendingQueueMessages(),
        whatsappApi.getQueueHistory()
      ]);

      setStats(statsResponse.data);
      setPendingMessages(pendingResponse.data.messages || []);
      setHistory(historyResponse.data.history || []);
      setLastUpdatedAt(new Date());
    } catch (error) {
      console.error('Impossible de charger la file WhatsApp:', error);
      message.error('Impossible de charger la file WhatsApp. Réessayez dans un instant.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  /**
   * Charge les informations de file à l'ouverture de la page. Aucun rafraîchissement
   * automatique n'est lancé afin d'éviter des requêtes inutiles ; le bouton permet
   * à l'utilisateur de contrôler le moment où il veut consulter l'état le plus récent.
   */
  useEffect(() => {
    loadQueueData(true);
  }, [loadQueueData]);

  /**
   * Annule un message encore en attente, puis recharge toute la vue pour mettre
   * immédiatement à jour les compteurs, le tableau et l'historique affichés.
   * @param {string} messageId Identifiant du message choisi par l'utilisateur.
   */
  const cancelMessage = async (messageId) => {
    setCancellingId(messageId);
    try {
      await whatsappApi.cancelQueueMessage(messageId);
      message.success('Le message a été annulé.');
      await loadQueueData();
    } catch (error) {
      console.error('Impossible d’annuler le message WhatsApp:', error);
      message.error(error.response?.data?.error || 'Impossible d’annuler ce message.');
    } finally {
      setCancellingId(null);
    }
  };

  /**
   * Ouvre une fenêtre dédiée au contenu complet d'un message. Ce choix évite
   * de tronquer le texte dans le tableau, particulièrement sur petit écran,
   * tout en conservant une table compacte pour le suivi opérationnel.
   * @param {object} queueMessage Message de file ou ligne d'historique sélectionné.
   */
  const openMessageContent = (queueMessage) => {
    setSelectedMessage(queueMessage);
  };

  /**
   * Colonnes de la file d'attente. La table conserve un défilement horizontal
   * sur petit écran afin de ne pas masquer d'informations ni casser la mise en page.
   */
  const pendingColumns = [
    {
      title: 'Destinataire',
      dataIndex: 'phone_number',
      key: 'phone_number',
      width: 160,
      render: (phone) => <Text strong>{phone}</Text>
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      width: 360,
      render: (content) => (
        <Tooltip title={content}>
          <Text ellipsis={{ tooltip: false }} style={{ display: 'block', maxWidth: 340 }}>
            {content}
          </Text>
        </Tooltip>
      )
    },
    {
      title: 'Priorité',
      dataIndex: 'priority',
      key: 'priority',
      width: 105,
      render: (priority) => <Tag color={priority <= 3 ? 'volcano' : 'blue'}>{priority}/10</Tag>
    },
    {
      title: 'Prévu pour',
      dataIndex: 'scheduled_for',
      key: 'scheduled_for',
      width: 175,
      render: formatDate
    },
    {
      title: 'Tentatives',
      dataIndex: 'attempts',
      key: 'attempts',
      width: 105,
      render: (attempts) => attempts || 0
    },
    {
      title: 'Action',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Button icon={<EyeOutlined aria-hidden="true" />} onClick={() => openMessageContent(record)}>
            Voir le contenu
          </Button>
          <Popconfirm
            title="Annuler ce message ?"
            description="Il ne sera pas envoyé par WhatsApp."
            okText="Annuler le message"
            cancelText="Retour"
            okButtonProps={{ danger: true }}
            onConfirm={() => cancelMessage(record.id)}
          >
            <Button
              danger
              icon={<DeleteOutlined aria-hidden="true" />}
              loading={cancellingId === record.id}
            >
              Annuler
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  /**
   * Colonnes de l'historique récent. Les nouveaux envois conservent leur texte
   * afin que l'utilisateur puisse ouvrir son contenu complet à tout moment.
   */
  const historyColumns = [
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 175,
      render: formatDate
    },
    {
      title: 'Destinataire',
      dataIndex: 'phone_number',
      key: 'phone_number',
      width: 160,
      render: (phone) => <Text strong>{phone}</Text>
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: renderHistoryStatus
    },
    {
      title: 'Tentatives',
      dataIndex: 'attempts',
      key: 'attempts',
      width: 105
    },
    {
      title: 'Contenu',
      key: 'message',
      width: 145,
      render: (_, record) => (
        <Tooltip title={record.message ? 'Voir le contenu complet' : 'Le contenu n’était pas conservé pour cet ancien envoi'}>
          <Button
            type="link"
            icon={<EyeOutlined aria-hidden="true" />}
            disabled={!record.message}
            onClick={() => openMessageContent(record)}
          >
            Voir
          </Button>
        </Tooltip>
      )
    },
    {
      title: 'Détail',
      dataIndex: 'error_message',
      key: 'error_message',
      render: (errorMessage) => errorMessage || '—'
    }
  ];

  const rateLimitInfo = stats?.rate_limit_info || {};

  return (
    <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Title level={isMobile ? 3 : 2} className="!mb-1">File d’envoi WhatsApp</Title>
          <Paragraph type="secondary" className="!mb-0">
            Suivez les messages à envoyer, annulez ceux qui ne doivent plus partir et consultez les résultats récents.
          </Paragraph>
        </div>
        <Space direction="vertical" size={4} className="sm:items-end">
          <Button
            type="primary"
            icon={<ReloadOutlined aria-hidden="true" />}
            loading={refreshing}
            onClick={() => loadQueueData()}
          >
            Actualiser
          </Button>
          <Text type="secondary" className="text-xs" aria-live="polite">
            Mis à jour : {lastUpdatedAt ? formatDate(lastUpdatedAt) : '—'}
          </Text>
        </Space>
      </div>

      {rateLimitInfo.is_blocked && (
        <Alert
          className="mb-6"
          type="warning"
          showIcon
          message="Les envois WhatsApp sont temporairement bloqués"
          description={rateLimitInfo.blocked_until ? `Blocage jusqu’au ${formatDate(rateLimitInfo.blocked_until)}.` : 'Vérifiez la configuration WhatsApp avant de reprendre les envois.'}
        />
      )}

      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={12} lg={6}>
          <Card loading={loading} size="small">
            <Statistic title="En attente" value={stats?.pending_count || 0} prefix={<ClockCircleOutlined aria-hidden="true" />} />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card loading={loading} size="small">
            <Statistic title="En cours" value={stats?.processing_count || 0} prefix={<SyncOutlined aria-hidden="true" />} />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card loading={loading} size="small">
            <Statistic title="Envoyés aujourd’hui" value={stats?.sent_today || 0} prefix={<CheckCircleOutlined aria-hidden="true" />} />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card loading={loading} size="small">
            <Statistic title="Échecs aujourd’hui" value={stats?.failed_today || 0} prefix={<CloseCircleOutlined aria-hidden="true" />} />
          </Card>
        </Col>
      </Row>

      <Card
        className="mb-6"
        title={<Space><SendOutlined aria-hidden="true" /> Messages en attente <Tag>{pendingMessages.length}</Tag></Space>}
        extra={<Text type="secondary">{stats ? `${stats.messages_last_minute || 0}/${rateLimitInfo.max_per_minute || 2} sur la dernière minute` : ''}</Text>}
      >
        {isMobile ? (
          /* Mobile : une carte par message (destinataire en haut, actions Voir/Annuler en bas) */
          <ColumnsCardList
            columns={pendingColumns}
            dataSource={pendingMessages}
            loading={loading}
            headerKeys={['phone_number']}
            actionKeys={['action']}
            clientPageSize={10}
            emptyText="Aucun message n’attend d’être envoyé"
          />
        ) : (
        <Table
          rowKey="id"
          columns={pendingColumns}
          dataSource={pendingMessages}
          loading={loading}
          scroll={{ x: 1100 }}
          pagination={{ pageSize: 10, hideOnSinglePage: true, showSizeChanger: false }}
          locale={{ emptyText: <Empty description="Aucun message n’attend d’être envoyé" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        />
        )}
      </Card>

      <Card title="Historique des dernières 24 heures">
        {isMobile ? (
          /* Mobile : une carte par envoi (destinataire en haut, puis date, statut, tentatives, contenu, détail) */
          <ColumnsCardList
            columns={historyColumns}
            dataSource={history}
            loading={loading}
            headerKeys={['phone_number']}
            clientPageSize={10}
            emptyText="Aucun envoi récent"
          />
        ) : (
        <Table
          rowKey="id"
          columns={historyColumns}
          dataSource={history}
          loading={loading}
          scroll={{ x: 960 }}
          pagination={{ pageSize: 10, hideOnSinglePage: true, showSizeChanger: false }}
          locale={{ emptyText: <Empty description="Aucun envoi récent" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        />
        )}
      </Card>

      <Modal
        title="Contenu du message WhatsApp"
        open={!!selectedMessage}
        onCancel={() => setSelectedMessage(null)}
        footer={<Button onClick={() => setSelectedMessage(null)}>Fermer</Button>}
        width={680}
      >
        <Text type="secondary">Destinataire : {selectedMessage?.phone_number || '—'}</Text>
        <Paragraph className="mt-4 whitespace-pre-wrap break-words text-base leading-6">
          {selectedMessage?.message || 'Le contenu de cet ancien message n’est pas disponible.'}
        </Paragraph>
      </Modal>
    </div>
  );
};

export default WhatsAppQueue;
